import { Icon, Menu } from "materia"
import { useTranslation } from "react-i18next"
import { modifiers } from "ui-tools"
import s from "./GroupMenu.module.scss"

// Passing a class replaces Menu.Item's own, so these rows are styled here in full.
const ITEM = s.menu__item
const DANGER_ITEM = modifiers(s, "menu__item", "danger")

export type GroupDialog =
  | "rename"
  | "add"
  | "members"
  | "admins"
  | "archive"
  | "leave"

type GroupMenuProps = {
  name: string
  /** Owner or admin (FAME-574). */
  isManager: boolean
  /** The server refuses renaming and adding while archived. */
  isArchived: boolean
  onOpen: (dialog: GroupDialog) => void
  /** Reversible, so it does not stop to ask. */
  onUnarchive: () => void
  /** The user's own Mute, open to every member. */
  isMuted: boolean
  onToggleMute: () => void
}

export function GroupMenu({
  name,
  isManager,
  isArchived,
  onOpen,
  onUnarchive,
  isMuted,
  onToggleMute,
}: GroupMenuProps) {
  const { t } = useTranslation()
  const canEdit = isManager && !isArchived

  return (
    <Menu>
      <Menu.Trigger asChild={true}>
        <button
          className={s.menu__trigger}
          type="button"
          aria-label={String(t("chat.GROUP_MENU_ARIA", { name }))}
        >
          <Icon icon="Dots" />
        </button>
      </Menu.Trigger>

      <Menu.Content anchor="bottom end" className={s.menu__content}>
        <Menu.Item
          as="button"
          type="button"
          className={ITEM}
          onClick={onToggleMute}
        >
          {t(isMuted ? "chat.UNMUTE_CONVERSATION" : "chat.MUTE_CONVERSATION")}
        </Menu.Item>
        {canEdit && (
          <Menu.Item
            as="button"
            type="button"
            className={ITEM}
            onClick={() => onOpen("rename")}
          >
            {t("chat.RENAME_GROUP")}
          </Menu.Item>
        )}
        {canEdit && (
          <Menu.Item
            as="button"
            type="button"
            className={ITEM}
            onClick={() => onOpen("add")}
          >
            {t("chat.ADD_MEMBERS")}
          </Menu.Item>
        )}
        <Menu.Item
          as="button"
          type="button"
          className={ITEM}
          onClick={() => onOpen("members")}
        >
          {t(isManager ? "chat.MANAGE_MEMBERS" : "chat.MEMBERS")}
        </Menu.Item>
        {isManager && (
          <Menu.Item
            as="button"
            type="button"
            className={ITEM}
            onClick={() => onOpen("admins")}
          >
            {t("chat.MANAGE_ADMINS")}
          </Menu.Item>
        )}

        <hr className={s.menu__divider} />

        {isManager &&
          (isArchived ? (
            <Menu.Item
              as="button"
              type="button"
              className={ITEM}
              onClick={onUnarchive}
            >
              {t("chat.UNARCHIVE_GROUP")}
            </Menu.Item>
          ) : (
            <Menu.Item
              as="button"
              type="button"
              className={DANGER_ITEM}
              onClick={() => onOpen("archive")}
            >
              {t("chat.ARCHIVE_GROUP")}
            </Menu.Item>
          ))}
        <Menu.Item
          as="button"
          type="button"
          className={DANGER_ITEM}
          onClick={() => onOpen("leave")}
        >
          {t("chat.LEAVE_GROUP")}
        </Menu.Item>
      </Menu.Content>
    </Menu>
  )
}
